import axios from "axios";
import { bugsnagClient } from "../bugsnag";
import adapter from "./adapter";
import { authFetch } from "./authFetch";
// Use relative URLs in production, absolute URLs in development
const API_URL =
  process.env.NODE_ENV === "production"
    ? ""
    : process.env.REACT_APP_BACKEND_URL || "http://localhost:5050";

// const API_URL = "https://app.monco.ai";
const api = {
  // Auth & User endpoints
  login: async (formData) => {
    const response = await fetch(`${API_URL}/api/users/login`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(formData),
    });
    return response.json();
  },

  register: async (formData) => {
    const response = await fetch(`${API_URL}/api/users/register`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(formData),
    });
    return response.json();
  },

  fetchUser: async () => {
    try {
      const response = await authFetch(`${API_URL}/api/users/me`);

      if (!response.ok) {
        throw new Error(`Server responded with status: ${response.status}`);
      }

      return adapter.user.me(await response.json());
    } catch (error) {
      console.error("Error fetching user:", error);
      bugsnagClient.notify(error);
      throw new Error("Failed to fetch user data");
    }
  },

  updateUser: async (formData) => {
    const response = await authFetch(`${API_URL}/api/users/update`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(formData),
    });
    return response.json();
  },

  changePassword: async ({ currentPassword, newPassword }) => {
    const response = await authFetch(`${API_URL}/api/users/change-password`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ currentPassword, newPassword }),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data.message || "Failed to change password");
    }
    return data;
  },

  forgotPassword: async ({ email, language }) => {
    const response = await fetch(`${API_URL}/api/users/forgot-password`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ email, language }),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      const error = new Error(
        data.message || "Failed to request password reset",
      );
      // Same error shape as resetPassword, so parseApiError works for both flows.
      error.status = response.status;
      error.errorCode = data.errorCode;
      throw error;
    }
    return data;
  },

  resetPassword: async ({ token, newPassword }) => {
    const response = await fetch(`${API_URL}/api/users/reset-password`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ token, newPassword }),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      const error = new Error(data.message || "Failed to reset password");
      // The reset page branches on the backend's machine-readable errorCode.
      error.status = response.status;
      error.errorCode = data.errorCode;
      throw error;
    }
    return data;
  },

  // Organization endpoints
  createOrganization: async (formData) => {
    const response = await authFetch(`${API_URL}/api/organizations`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(formData),
    });
    return response.json();
  },

  getMyOrganization: async () => {
    const response = await authFetch(`${API_URL}/api/organizations/my`);
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    return adapter.organization.getMyOrganization(await response.json());
  },

  updateOrganization: async (formData) => {
    const response = await authFetch(`${API_URL}/api/organizations/my`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(adapter.organization.updateOrganization(formData)),
    });
    return response.json();
  },

  inviteUserToOrganization: async (formData) => {
    const response = await authFetch(`${API_URL}/api/organizations/invite`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(formData),
    });
    return response.json();
  },

  // New invite validation endpoint (token -> org details)
  validateInvite: async (token) => {
    const response = await fetch(`${API_URL}/api/invites/validate`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token }),
    });
    return response.json();
  },

  // Accept invite endpoint (requires auth)
  acceptInvite: async (token) => {
    const response = await authFetch(`${API_URL}/api/invites/accept`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ token }),
    });
    return response.json();
  },

  acceptInvitation: async (token) => {
    const response = await authFetch(
      `${API_URL}/api/organizations/accept-invitation/${token}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );
    return response.json();
  },

  getOrganizationMembers: async () => {
    const response = await authFetch(`${API_URL}/api/organizations/members`);
    return adapter.organization.getOrganizationMembers(await response.json());
  },

  updateMemberRole: async (userId, role) => {
    const response = await authFetch(
      `${API_URL}/api/organizations/members/${userId}`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ role }),
      },
    );
    return response.json();
  },

  removeMember: async (userId) => {
    const response = await authFetch(
      `${API_URL}/api/organizations/members/${userId}`,
      {
        method: "DELETE",
      },
    );
    return response.json();
  },

  getOrganizationProjects: async (params = {}) => {
    const queryParams = new URLSearchParams(params);
    const response = await authFetch(
      `${API_URL}/api/organizations/projects?${queryParams}`,
    );
    return response.json();
  },

  getOrganizationStats: async () => {
    const response = await authFetch(`${API_URL}/api/organizations/stats`);
    return response.json();
  },

  // Global Admin endpoints
  getGlobalAdminDashboard: async () => {
    const response = await authFetch(`${API_URL}/api/admin/dashboard`);
    return adapter.admin.dashboard(await response.json());
  },

  getAllOrganizations: async (params = {}) => {
    const queryParams = new URLSearchParams(params);
    const response = await authFetch(
      `${API_URL}/api/admin/organizations?${queryParams}`,
    );
    return adapter.admin.listOrganizations(await response.json());
  },

  getOrganizationDetails: async (orgId) => {
    const response = await authFetch(
      `${API_URL}/api/admin/organizations/${orgId}`,
    );
    return response.json();
  },

  updateOrganizationAsAdmin: async (orgId, formData) => {
    const response = await authFetch(
      `${API_URL}/api/admin/organizations/${orgId}`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      },
    );
    return response.json();
  },

  createOrganizationAsAdmin: async (formData) => {
    const response = await authFetch(`${API_URL}/api/admin/organizations`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(adapter.admin.createOrganization(formData)),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(
        data.message || `HTTP ${response.status}: ${response.statusText}`,
      );
    }

    return data;
  },

  deleteOrganization: async (orgId) => {
    const response = await authFetch(
      `${API_URL}/api/admin/organizations/${orgId}`,
      {
        method: "DELETE",
      },
    );
    return response.json();
  },

  getAllProjectsAsAdmin: async (params = {}) => {
    const queryParams = new URLSearchParams(params);
    const response = await authFetch(
      `${API_URL}/api/admin/projects?${queryParams}`,
    );
    return adapter.admin.listProjects(await response.json());
  },

  getAllUsersAsAdmin: async (params = {}) => {
    const queryParams = new URLSearchParams(params);
    const response = await authFetch(
      `${API_URL}/api/admin/users?${queryParams}`,
    );
    return adapter.admin.listUsers(await response.json());
  },

  toggleGlobalAdmin: async (userId, isGlobalAdmin) => {
    const response = await authFetch(
      `${API_URL}/api/admin/users/${userId}/global-admin`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ isGlobalAdmin }),
      },
    );
    return response.json();
  },

  updateOrganizationRole: async (userId, role) => {
    const response = await authFetch(
      `${API_URL}/api/admin/users/${userId}/organization-role`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ role }),
      },
    );
    return response.json();
  },

  transferUserOrganization: async (userId, organizationId, role = "member") => {
    const response = await authFetch(
      `${API_URL}/api/admin/users/${userId}/organization`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ organizationId, role }),
      },
    );

    const data = await response.json();

    if (!response.ok) {
      throw new Error(
        data.message || `HTTP ${response.status}: ${response.statusText}`,
      );
    }

    return data;
  },

  getAnalytics: async (period = "30") => {
    const response = await authFetch(
      `${API_URL}/api/admin/analytics?period=${period}`,
    );
    return response.json();
  },

  // Project endpoints
  createProject: async ({ name, description, organizationId, members }) => {
    const response = await authFetch(`${API_URL}/api/projects`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ name, description, organizationId, members }),
    });

    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      const message = Array.isArray(data.message)
        ? data.message.join(", ")
        : data.message ||
          data.errors?.map((error) => error.msg).join(", ") ||
          `Server responded with status: ${response.status}`;
      throw new Error(message);
    }
    return data;
  },

  uploadImages: async (projectId, formData) => {
    const response = await authFetch(`${API_URL}/api/uploads/images`, {
      method: "POST",
      body: formData,
    });
    return response.json();
  },

  fetchProjects: async ({ cursor, limit, archived, organization } = {}) => {
    const params = new URLSearchParams();
    if (cursor) params.set("cursor", cursor);
    if (limit) params.set("limit", String(limit));
    if (archived) params.set("archived", "true");
    if (organization) params.set("organization", String(organization));
    const query = params.toString();
    const response = await authFetch(
      `${API_URL}/api/projects${query ? `?${query}` : ""}`,
    );
    return adapter.project.list(await response.json());
  },

  archiveProject: async (projectId) => {
    const response = await authFetch(
      `${API_URL}/api/projects/${projectId}/archive`,
      { method: "PATCH" },
    );
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.message || "Failed to archive project");
    }
    return response.json();
  },

  unarchiveProject: async (projectId) => {
    const response = await authFetch(
      `${API_URL}/api/projects/${projectId}/unarchive`,
      { method: "PATCH" },
    );
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.message || "Failed to unarchive project");
    }
    return response.json();
  },

  deleteProject: async (projectId) => {
    const response = await authFetch(`${API_URL}/api/projects/${projectId}`, {
      method: "DELETE",
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.message || "Failed to delete project");
    }
    return response.json();
  },

  updateProject: async (projectId, { name, description, teamChanges }) => {
    const response = await authFetch(`${API_URL}/api/projects/${projectId}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ name, description, ...teamChanges }),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.message || "Failed to update project");
    }
    return response.json();
  },

  fetchProcessingStatus: async (jobId) => {
    const response = await authFetch(
      `${API_URL}/api/uploads/processing-status/${jobId}`,
    );
    return response.json();
  },

  fetchProjectJobs: async (projectId) => {
    const response = await authFetch(
      `${API_URL}/api/uploads/project-jobs/${projectId}`,
    );
    return response.json();
  },

  fetchProject: async (id) => {
    // Validate ID before making request
    if (!id || id === "undefined" || id === "null") {
      throw new Error("Invalid project ID provided");
    }

    const response = await authFetch(`${API_URL}/api/projects/${id}`);
    return adapter.project.getProject(await response.json());
  },

  getProjectAssetUrls: async (projectId, keys) => {
    const response = await authFetch(
      `${API_URL}/api/projects/${projectId}/asset-urls`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ keys }),
      },
    );

    if (!response.ok) {
      throw new Error(`Failed to sign asset urls: ${response.status}`);
    }

    return response.json();
  },

  // PDS Integration endpoints
  fetchPdsProjects: async (page = 0, entriesPerPage = 20, search = "") => {
    const params = new URLSearchParams({
      page: String(page),
      entriesPerPage: String(entriesPerPage),
    });
    if (search) params.set("search", search);
    const response = await authFetch(
      `${API_URL}/api/projects/pds/projects?${params}`,
    );
    if (!response.ok) {
      throw new Error("Failed to fetch PDS projects");
    }
    return response.json();
  },

  fetchLinkedPdsProjectIds: async () => {
    const response = await authFetch(`${API_URL}/api/projects/pds/linked-ids`);
    if (!response.ok) {
      throw new Error("Failed to fetch linked PDS project IDs");
    }
    return response.json();
  },

  fetchPdsProjectByUuid: async (uuid) => {
    const response = await authFetch(
      `${API_URL}/api/projects/pds/projects/${encodeURIComponent(uuid)}`,
    );
    if (!response.ok) {
      throw new Error("Failed to fetch PDS project details");
    }
    return response.json();
  },

  linkPdsProject: async (projectId, pdsProjectId) => {
    const response = await authFetch(
      `${API_URL}/api/projects/${projectId}/pds-link`,
      {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ pdsProjectId }),
      },
    );
    if (!response.ok) {
      throw new Error("Failed to link PDS project");
    }
    return response.json();
  },

  unlinkPdsProject: async (projectId) => {
    const response = await authFetch(
      `${API_URL}/api/projects/${projectId}/pds-link`,
      {
        method: "DELETE",
      },
    );
    if (!response.ok) {
      throw new Error("Failed to unlink PDS project");
    }
    return response.json();
  },

  uploadVideo: async (projectId, formData) => {
    const response = await authFetch(`${API_URL}/api/uploads/video`, {
      method: "POST",
      body: formData,
    });
    return response.json();
  },

  // New: Presign + direct upload flow for videos
  presignVideoUpload: async (projectId, sectionId, fileName, contentType) => {
    const response = await authFetch(`${API_URL}/api/uploads/video/presign`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ projectId, sectionId, fileName, contentType }),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || `Presign failed: ${response.status}`,
      );
    }
    return response.json();
  },

  // Multipart video upload APIs
  initiateMultipartVideo: async (
    projectId,
    sectionId,
    fileName,
    contentType,
  ) => {
    const response = await authFetch(
      `${API_URL}/api/uploads/video/multipart/initiate`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(
          adapter.documentation.initiateVideoUpload({
            projectId,
            sectionId,
            fileName,
            contentType,
          }),
        ),
      },
    );
    if (!response.ok)
      throw new Error(`Initiate multipart failed: ${response.status}`);
    return response.json();
  },
  presignMultipartPart: async (key, uploadId, partNumber) => {
    const response = await authFetch(
      `${API_URL}/api/uploads/video/multipart/part`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ key, uploadId, partNumber }),
      },
    );
    if (!response.ok)
      throw new Error(`Presign part failed: ${response.status}`);
    return response.json();
  },
  completeMultipartVideo: async (
    projectId,
    sectionId,
    key,
    uploadId,
    parts,
  ) => {
    const response = await authFetch(
      `${API_URL}/api/uploads/video/multipart/complete`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(
          adapter.documentation.completeVideoUpload({
            projectId,
            sectionId,
            key,
            uploadId,
            parts,
          }),
        ),
      },
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || `Complete multipart failed: ${response.status}`,
      );
    }
    return response.json();
  },
  // Quantity Surveyor multipart upload (SmartSurveyor)
  initiateQuantitySurveyorMultipart: async (
    projectId,
    fileName,
    contentType,
  ) => {
    const response = await authFetch(
      `${API_URL}/api/uploads/quantity-surveyor/multipart/initiate`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ projectId, fileName, contentType }),
      },
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message ||
          `Initiate quantity-surveyor multipart failed: ${response.status}`,
      );
    }

    return response.json();
  },

  completeQuantitySurveyorMultipart: async (projectId, files) => {
    const response = await authFetch(
      `${API_URL}/api/uploads/quantity-surveyor/multipart/complete`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ projectId, files }),
      },
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message ||
          `Complete quantity-surveyor multipart failed: ${response.status}`,
      );
    }

    return response.json();
  },

  getQuantitySurveyorUploadsByProject: async (projectId) => {
    const response = await authFetch(
      `${API_URL}/api/quantity-surveyor-uploads/project/${projectId}`,
      {
        headers: {
          "content-type": "application/json",
        },
      },
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message ||
          `Failed to fetch quantity-surveyor uploads: ${response.status}`,
      );
    }

    return response.json();
  },

  getQuantitySurveyorUnfinishedUploadFiles: async (projectId) => {
    const response = await authFetch(
      `${API_URL}/api/quantity-surveyor-uploads/project/${projectId}/unfinished-files`,
      {
        headers: {
          "content-type": "application/json",
        },
      },
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message ||
          `Failed to fetch unfinished upload files: ${response.status}`,
      );
    }

    return response.json();
  },

  getQuantitySurveyorDistributionBoardFilesByProject: async (projectId) => {
    const response = await authFetch(
      `${API_URL}/api/quantity-surveyor-distribution-board-files?projectId=${projectId}`,
      {
        headers: {
          "content-type": "application/json",
        },
      },
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message ||
          `Failed to fetch quantity-surveyor distribution board files: ${response.status}`,
      );
    }

    return response.json();
  },

  getQuantitySurveyorFloorPlansByProject: async (
    projectId,
    { archived = false } = {},
  ) => {
    const archivedParam = archived ? "&archived=true" : "";
    const response = await authFetch(
      `${API_URL}/api/quantity-surveyor-floor-plans?projectId=${projectId}${archivedParam}`,
      {
        headers: {
          "content-type": "application/json",
        },
      },
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message ||
          `Failed to fetch quantity-surveyor floor plans: ${response.status}`,
      );
    }

    return response.json();
  },

  archiveQuantitySurveyorFloorPlan: async (floorPlanId) => {
    const response = await authFetch(
      `${API_URL}/api/quantity-surveyor-floor-plans/${floorPlanId}/archive`,
      { method: "PATCH" },
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || `Failed to archive floor plan: ${response.status}`,
      );
    }
    return response.json();
  },

  unarchiveQuantitySurveyorFloorPlan: async (floorPlanId) => {
    const response = await authFetch(
      `${API_URL}/api/quantity-surveyor-floor-plans/${floorPlanId}/unarchive`,
      { method: "PATCH" },
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message ||
          `Failed to unarchive floor plan: ${response.status}`,
      );
    }
    return response.json();
  },

  deleteQuantitySurveyorFloorPlan: async (floorPlanId) => {
    const response = await authFetch(
      `${API_URL}/api/quantity-surveyor-floor-plans/${floorPlanId}`,
      { method: "DELETE" },
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || `Failed to delete floor plan: ${response.status}`,
      );
    }
    return response.json();
  },

  getQuantitySurveyorFloorPlanStructuredData: async (floorPlanId) => {
    const response = await authFetch(
      `${API_URL}/api/quantity-surveyor-floor-plans/${floorPlanId}/structured-data`,
      {
        headers: {
          "content-type": "application/json",
        },
      },
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message ||
          `Failed to fetch floor plan structured data: ${response.status}`,
      );
    }

    return response.json();
  },

  updateQuantitySurveyorFloorPlan: async (floorPlanId, { name }) => {
    const response = await authFetch(
      `${API_URL}/api/quantity-surveyor-floor-plans/${floorPlanId}`,
      {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name }),
      },
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || `Failed to update floor plan: ${response.status}`,
      );
    }
    return response.json();
  },

  setQuantitySurveyorFloorPlanFolder: async (floorPlanId, folderId) => {
    const response = await authFetch(
      `${API_URL}/api/quantity-surveyor-floor-plans/${floorPlanId}/folder`,
      {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ folderId }),
      },
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message ||
          `Failed to move floor plan to folder: ${response.status}`,
      );
    }
    return response.json();
  },

  getQuantitySurveyorFloorPlanFolders: async (projectId) => {
    const response = await authFetch(
      `${API_URL}/api/quantity-surveyor-floor-plan-folders?projectId=${projectId}`,
      {
        headers: {
          "content-type": "application/json",
        },
      },
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message ||
          `Failed to fetch floor plan folders: ${response.status}`,
      );
    }
    return response.json();
  },

  createQuantitySurveyorFloorPlanFolder: async ({ projectId, name }) => {
    const response = await authFetch(
      `${API_URL}/api/quantity-surveyor-floor-plan-folders`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ projectId, name }),
      },
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const error = new Error(
        errorData.message ||
          `Failed to create floor plan folder: ${response.status}`,
      );
      // 409 = the name is already taken in this project; read via parseApiError.
      error.status = response.status;
      throw error;
    }
    return response.json();
  },

  renameQuantitySurveyorFloorPlanFolder: async (folderId, { name }) => {
    const response = await authFetch(
      `${API_URL}/api/quantity-surveyor-floor-plan-folders/${folderId}`,
      {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name }),
      },
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const error = new Error(
        errorData.message ||
          `Failed to rename floor plan folder: ${response.status}`,
      );
      // 409 = the name is already taken in this project; read via parseApiError.
      error.status = response.status;
      throw error;
    }
    return response.json();
  },

  deleteQuantitySurveyorFloorPlanFolder: async (folderId) => {
    const response = await authFetch(
      `${API_URL}/api/quantity-surveyor-floor-plan-folders/${folderId}`,
      { method: "DELETE" },
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message ||
          `Failed to delete floor plan folder: ${response.status}`,
      );
    }
  },

  suggestQuantitySurveyorFloorPlanFolders: async (projectId, signal) => {
    const response = await authFetch(
      `${API_URL}/api/quantity-surveyor-floor-plan-folders/suggestion`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ projectId: Number(projectId) }),
        // Closing the modal aborts the request, and the backend stops the model calls
        signal,
      },
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const error = new Error(
        errorData.message ||
          `Failed to suggest floor plan folders: ${response.status}`,
      );
      // "tooManyPlans" above the plan limit; read via parseApiError.
      error.errorCode = errorData.code;
      throw error;
    }
    return response.json();
  },

  importQuantitySurveyorFloorPlanFolders: async ({
    projectId,
    overwrite,
    folders,
  }) => {
    const response = await authFetch(
      `${API_URL}/api/quantity-surveyor-floor-plan-folders/import`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          projectId: Number(projectId),
          overwrite,
          folders,
        }),
      },
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const error = new Error(
        errorData.message ||
          `Failed to import floor plan folders: ${response.status}`,
      );
      // "unknownFloorPlans" when a listed plan no longer exists; read via parseApiError.
      error.errorCode = errorData.code;
      throw error;
    }
    return response.json();
  },

  getQuantitySurveyorFloorPlanComparison: async (floorPlanId) => {
    const response = await authFetch(
      `${API_URL}/api/quantity-surveyor-floor-plans/${floorPlanId}/comparison`,
      {
        headers: {
          "content-type": "application/json",
        },
      },
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message ||
          `Failed to fetch floor plan comparison: ${response.status}`,
      );
    }
    return response.json();
  },

  setQuantitySurveyorFloorPlanComparison: async (
    floorPlanId,
    comparedFloorPlanId,
  ) => {
    const response = await authFetch(
      `${API_URL}/api/quantity-surveyor-floor-plans/${floorPlanId}/comparison`,
      {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ comparedFloorPlanId }),
      },
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message ||
          `Failed to set floor plan comparison: ${response.status}`,
      );
    }
    return response.json();
  },

  createQuantitySurveyorElementType: async ({ floorPlanId, name }) => {
    const response = await authFetch(
      `${API_URL}/api/quantity-surveyor-element-types`,
      {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ floorPlanId, name }),
      },
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message ||
          `Failed to create element type: ${response.status}`,
      );
    }
    return response.json();
  },

  createQuantitySurveyorElementMarker: async ({
    elementTypeId,
    pageId,
    bboxX1,
    bboxY1,
    bboxX2,
    bboxY2,
  }) => {
    const response = await authFetch(
      `${API_URL}/api/quantity-surveyor-element-markers`,
      {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          elementTypeId,
          pageId,
          bboxX1,
          bboxY1,
          bboxX2,
          bboxY2,
        }),
      },
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message ||
          `Failed to create element marker: ${response.status}`,
      );
    }
    return response.json();
  },

  bulkCreateQuantitySurveyorElementMarkers: async ({
    floorPlanId,
    pageId,
    markers,
  }) => {
    const response = await authFetch(
      `${API_URL}/api/quantity-surveyor-element-markers/bulk`,
      {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ floorPlanId, pageId, markers }),
      },
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message ||
          `Failed to batch create element markers: ${response.status}`,
      );
    }
    return response.json();
  },

  updateQuantitySurveyorPageCalibration: async ({
    pageId,
    metersPerPdfPoint,
  }) => {
    const response = await authFetch(
      `${API_URL}/api/quantity-surveyor-floor-plan-pages/${pageId}/calibration`,
      {
        method: "PATCH",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ metersPerPdfPoint }),
      },
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message ||
          `Failed to update page calibration: ${response.status}`,
      );
    }
    return response.json();
  },

  updateQuantitySurveyorPageRotation: async ({ pageId, rotation }) => {
    const response = await authFetch(
      `${API_URL}/api/quantity-surveyor-floor-plan-pages/${pageId}/rotation`,
      {
        method: "PATCH",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ rotation }),
      },
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message ||
          `Failed to update page rotation: ${response.status}`,
      );
    }
    return response.json();
  },

  bulkCreateQuantitySurveyorRoutes: async ({ floorPlanId, pageId, routes }) => {
    const response = await authFetch(
      `${API_URL}/api/quantity-surveyor-routes/bulk`,
      {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ floorPlanId, pageId, routes }),
      },
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message ||
          `Failed to batch create routes: ${response.status}`,
      );
    }
    return response.json();
  },

  getQuantitySurveyorFloorPlanLayout: async (floorPlanId) => {
    const response = await authFetch(
      `${API_URL}/api/quantity-surveyor-floor-plan-layouts?floorPlanId=${floorPlanId}`,
      {
        headers: { "content-type": "application/json" },
      },
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message ||
          `Failed to fetch floor plan layout: ${response.status}`,
      );
    }
    return response.json();
  },

  createQuantitySurveyorFloorPlanRoom: async ({ pageId, name, points }) => {
    const response = await authFetch(
      `${API_URL}/api/quantity-surveyor-floor-plan-rooms`,
      {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ pageId, name, points }),
      },
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message ||
          `Failed to create floor plan room: ${response.status}`,
      );
    }
    return response.json();
  },

  bulkCreateQuantitySurveyorFloorPlanRooms: async ({ pageId, rooms }) => {
    const response = await authFetch(
      `${API_URL}/api/quantity-surveyor-floor-plan-rooms/bulk`,
      {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ pageId, rooms }),
      },
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message ||
          `Failed to batch create floor plan rooms: ${response.status}`,
      );
    }
    return response.json();
  },

  mergeQuantitySurveyorFloorPlanRooms: async ({
    pageId,
    roomIds,
    name,
    points,
  }) => {
    const response = await authFetch(
      `${API_URL}/api/quantity-surveyor-floor-plan-rooms/merge`,
      {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ pageId, roomIds, name, points }),
      },
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message ||
          `Failed to merge floor plan rooms: ${response.status}`,
      );
    }
    return response.json();
  },

  updateQuantitySurveyorFloorPlanRoom: async (roomId, payload) => {
    const response = await authFetch(
      `${API_URL}/api/quantity-surveyor-floor-plan-rooms/${roomId}`,
      {
        method: "PATCH",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(payload),
      },
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message ||
          `Failed to update floor plan room: ${response.status}`,
      );
    }
    return response.json();
  },

  deleteQuantitySurveyorFloorPlanRoom: async (roomId) => {
    const response = await authFetch(
      `${API_URL}/api/quantity-surveyor-floor-plan-rooms/${roomId}`,
      { method: "DELETE" },
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message ||
          `Failed to delete floor plan room: ${response.status}`,
      );
    }
  },

  createQuantitySurveyorSelectionArea: async ({
    pageId,
    points,
    appliesToElements,
    appliesToRoutes,
    mode,
  }) => {
    const response = await authFetch(
      `${API_URL}/api/quantity-surveyor-selection-areas`,
      {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          pageId,
          points,
          appliesToElements,
          appliesToRoutes,
          mode,
        }),
      },
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message ||
          `Failed to create selection area: ${response.status}`,
      );
    }
    return response.json();
  },

  updateQuantitySurveyorSelectionArea: async (selectionAreaId, payload) => {
    const response = await authFetch(
      `${API_URL}/api/quantity-surveyor-selection-areas/${selectionAreaId}`,
      {
        method: "PATCH",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(payload),
      },
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message ||
          `Failed to update selection area: ${response.status}`,
      );
    }
    return response.json();
  },

  deleteQuantitySurveyorSelectionArea: async (selectionAreaId) => {
    const response = await authFetch(
      `${API_URL}/api/quantity-surveyor-selection-areas/${selectionAreaId}`,
      {
        method: "DELETE",
      },
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message ||
          `Failed to delete selection area: ${response.status}`,
      );
    }
    return response.json();
  },

  updateQuantitySurveyorElementType: async (elementTypeId, payload) => {
    const response = await authFetch(
      `${API_URL}/api/quantity-surveyor-element-types/${elementTypeId}`,
      {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      },
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message ||
          `Failed to update element type: ${response.status}`,
      );
    }
    return response.json();
  },

  renameQuantitySurveyorElementType: async (
    elementTypeId,
    name,
    applyToProject = false,
  ) => {
    const response = await authFetch(
      `${API_URL}/api/quantity-surveyor-element-types/${elementTypeId}/rename`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, applyToProject }),
      },
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message ||
          `Failed to rename element type: ${response.status}`,
      );
    }
    return response.json();
  },

  getQuantitySurveyorElementTypeRenamePreview: async (
    elementTypeId,
    newName,
  ) => {
    const response = await authFetch(
      `${API_URL}/api/quantity-surveyor-element-types/${elementTypeId}/rename-preview?newName=${encodeURIComponent(newName)}`,
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message ||
          `Failed to load rename preview: ${response.status}`,
      );
    }
    return response.json();
  },

  updateQuantitySurveyorRouteType: async (routeTypeId, payload) => {
    const response = await authFetch(
      `${API_URL}/api/quantity-surveyor-route-types/${routeTypeId}`,
      {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      },
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || `Failed to update route type: ${response.status}`,
      );
    }
    return response.json();
  },

  updateQuantitySurveyorDiagramItemType: async (itemTypeId, payload) => {
    const response = await authFetch(
      `${API_URL}/api/quantity-surveyor-diagram-item-types/${itemTypeId}`,
      {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      },
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message ||
          `Failed to update diagram item type: ${response.status}`,
      );
    }
    return response.json();
  },

  deleteQuantitySurveyorElementType: async (elementTypeId) => {
    const response = await authFetch(
      `${API_URL}/api/quantity-surveyor-element-types/${elementTypeId}`,
      { method: "DELETE" },
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message ||
          `Failed to delete element type: ${response.status}`,
      );
    }
  },

  deleteQuantitySurveyorRouteType: async (routeTypeId) => {
    const response = await authFetch(
      `${API_URL}/api/quantity-surveyor-route-types/${routeTypeId}`,
      { method: "DELETE" },
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || `Failed to delete route type: ${response.status}`,
      );
    }
  },

  deleteQuantitySurveyorDiagramItemType: async (diagramItemTypeId) => {
    const response = await authFetch(
      `${API_URL}/api/quantity-surveyor-diagram-item-types/${diagramItemTypeId}`,
      { method: "DELETE" },
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message ||
          `Failed to delete diagram item type: ${response.status}`,
      );
    }
  },

  deleteQuantitySurveyorElementMarker: async (markerId) => {
    const response = await authFetch(
      `${API_URL}/api/quantity-surveyor-element-markers/${markerId}`,
      { method: "DELETE" },
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message ||
          `Failed to delete element marker: ${response.status}`,
      );
    }

    return response.json();
  },

  updateQuantitySurveyorElementMarker: async (markerId, data) => {
    const response = await authFetch(
      `${API_URL}/api/quantity-surveyor-element-markers/${markerId}`,
      {
        method: "PATCH",
        headers: {
          "content-type": "application/json",
        },
        body: JSON.stringify(data),
      },
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message ||
          `Failed to update element marker: ${response.status}`,
      );
    }

    return response.json();
  },

  setQuantitySurveyorElementMarkerNote: async (markerId, { content }) => {
    const response = await authFetch(
      `${API_URL}/api/quantity-surveyor-element-markers/${markerId}/note`,
      {
        method: "PUT",
        headers: {
          "content-type": "application/json",
        },
        body: JSON.stringify({ content }),
      },
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message ||
          `Failed to set element marker note: ${response.status}`,
      );
    }

    return response.json();
  },

  deleteQuantitySurveyorElementMarkerNote: async (markerId) => {
    const response = await authFetch(
      `${API_URL}/api/quantity-surveyor-element-markers/${markerId}/note`,
      {
        method: "DELETE",
      },
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message ||
          `Failed to delete element marker note: ${response.status}`,
      );
    }

    return response.json();
  },

  bulkDeleteQuantitySurveyorElementMarkers: async ({
    floorPlanId,
    markerIds,
  }) => {
    const response = await authFetch(
      `${API_URL}/api/quantity-surveyor-element-markers/bulk-delete`,
      {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ floorPlanId, markerIds }),
      },
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message ||
          `Failed to bulk delete element markers: ${response.status}`,
      );
    }

    return response.json();
  },

  bulkMoveQuantitySurveyorElementMarkers: async ({
    floorPlanId,
    markerIds,
    elementTypeName,
  }) => {
    const response = await authFetch(
      `${API_URL}/api/quantity-surveyor-element-markers/bulk-move`,
      {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ floorPlanId, markerIds, elementTypeName }),
      },
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message ||
          `Failed to bulk move element markers: ${response.status}`,
      );
    }

    return response.json();
  },

  getQuantitySurveyorDistributionBoardFileStructuredData: async (
    distBoardFileId,
  ) => {
    const response = await authFetch(
      `${API_URL}/api/quantity-surveyor-distribution-board-files/${distBoardFileId}/structured-data`,
      {
        headers: {
          "content-type": "application/json",
        },
      },
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message ||
          `Failed to fetch distribution board file structured data: ${response.status}`,
      );
    }

    return response.json();
  },

  getQuantitySurveyorDiagramsByProject: async (projectId) => {
    const response = await authFetch(
      `${API_URL}/api/quantity-surveyor-diagrams?projectId=${projectId}`,
      {
        headers: {
          "content-type": "application/json",
        },
      },
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message ||
          `Failed to fetch diagrams by project: ${response.status}`,
      );
    }

    return response.json();
  },

  getQuantitySurveyorDiagramStructuredData: async (diagramId) => {
    const response = await authFetch(
      `${API_URL}/api/quantity-surveyor-distribution-board-files/diagrams/${diagramId}/structured-data`,
      {
        headers: {
          "content-type": "application/json",
        },
      },
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message ||
          `Failed to fetch diagram structured data: ${response.status}`,
      );
    }

    return response.json();
  },

  getQuantitySurveyorAggregatedExportData: async (projectId) => {
    const response = await authFetch(
      `${API_URL}/api/quantity-surveyor-export-data/${projectId}/aggregated`,
      {
        headers: {
          "content-type": "application/json",
        },
      },
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message ||
          `Failed to fetch aggregated export data: ${response.status}`,
      );
    }

    return response.json();
  },

  getQuantitySurveyorPerPlanExportData: async (projectId) => {
    const response = await authFetch(
      `${API_URL}/api/quantity-surveyor-export-data/${projectId}/per-plan`,
      {
        headers: {
          "content-type": "application/json",
        },
      },
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message ||
          `Failed to fetch per-plan export data: ${response.status}`,
      );
    }

    return response.json();
  },

  streamQuantitySurveyorDuplicateNames: async (projectId, signal) => {
    const response = await authFetch(
      `${API_URL}/api/quantity-surveyor-duplicate-names/find`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ projectId }),
        // Closing the stream stops the search on the backend
        signal,
      },
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message ||
          `Failed to find duplicate names: ${response.status}`,
      );
    }
    return response;
  },

  applyQuantitySurveyorDuplicateNames: async (projectId, renames) => {
    const response = await authFetch(
      `${API_URL}/api/quantity-surveyor-duplicate-names/apply`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ projectId, renames }),
      },
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const error = new Error(
        errorData.message ||
          `Failed to apply duplicate renames: ${response.status}`,
      );
      // The 400 body carries a machine-readable code the UI can translate
      error.code = errorData.code;
      throw error;
    }
    return response.json();
  },

  streamQuantitySurveyorLvRematchFind: async (projectId, language, signal) => {
    const response = await authFetch(
      `${API_URL}/api/quantity-surveyor-lv-rematch/find`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ projectId, language }),
        // Closing the stream aborts the matching on the backend
        signal,
      },
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message ||
          `Failed to find LV re-match proposals: ${response.status}`,
      );
    }
    return response;
  },

  applyQuantitySurveyorLvRematch: async ({ projectId, changes }) => {
    const response = await authFetch(
      `${API_URL}/api/quantity-surveyor-lv-rematch/apply`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ projectId, changes }),
      },
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message ||
          `Failed to apply LV re-match changes: ${response.status}`,
      );
    }
    return response.json();
  },

  getQuantitySurveyorItemTypeSettings: async (projectId) => {
    const response = await authFetch(
      `${API_URL}/api/quantity-surveyor-item-type-settings/${projectId}/settings`,
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message ||
          `Failed to load item type settings: ${response.status}`,
      );
    }
    return response.json();
  },

  setQuantitySurveyorItemTypeUnit: async (
    projectId,
    { kind, floorPlanItemTypeName, unit },
  ) => {
    const response = await authFetch(
      `${API_URL}/api/quantity-surveyor-item-type-settings/set-unit`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ projectId, kind, floorPlanItemTypeName, unit }),
      },
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || `Failed to set item type unit: ${response.status}`,
      );
    }
    return response.json();
  },

  setQuantitySurveyorItemTypeManualPrice: async (
    projectId,
    { kind, floorPlanItemTypeName, factor, property, value },
  ) => {
    const response = await authFetch(
      `${API_URL}/api/quantity-surveyor-item-type-settings/set-manual-price`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          projectId,
          kind,
          floorPlanItemTypeName,
          factor,
          property,
          value,
        }),
      },
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message ||
          `Failed to set item type manual price: ${response.status}`,
      );
    }
    return response.json();
  },

  streamQuantitySurveyorItemTypePrices: async (projectId, mode, signal) => {
    const response = await authFetch(
      `${API_URL}/api/quantity-surveyor-item-type-settings/suggest-prices`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ projectId, mode }),
        // Closing the stream stops new pricing batches on the backend
        signal,
      },
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message ||
          `Failed to suggest item type prices: ${response.status}`,
      );
    }
    return response;
  },

  suggestQuantitySurveyorItemTypePrice: async (
    projectId,
    { kind, floorPlanItemTypeName },
    signal,
  ) => {
    const response = await authFetch(
      `${API_URL}/api/quantity-surveyor-item-type-settings/suggest-price`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ projectId, kind, floorPlanItemTypeName }),
        // Closing the overview aborts the request, and the backend stops pricing
        signal,
      },
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message ||
          `Failed to suggest the item type price: ${response.status}`,
      );
    }
    return response.json();
  },

  openOverviewAgentThread: async (projectId) => {
    const response = await authFetch(
      `${API_URL}/api/quantity-surveyor-overview-agent/threads/latest?projectId=${projectId}`,
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message ||
          `Failed to open overview agent thread: ${response.status}`,
      );
    }
    return response.json();
  },

  createOverviewAgentThread: async (projectId) => {
    const response = await authFetch(
      `${API_URL}/api/quantity-surveyor-overview-agent/threads`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ projectId }),
      },
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message ||
          `Failed to create overview agent thread: ${response.status}`,
      );
    }
    return response.json();
  },

  loadOverviewAgentThread: async (projectId, threadId) => {
    const response = await authFetch(
      `${API_URL}/api/quantity-surveyor-overview-agent/threads/${threadId}?projectId=${projectId}`,
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message ||
          `Failed to load overview agent thread: ${response.status}`,
      );
    }
    return response.json();
  },

  // The turn arrives as a stream of events, so the caller reads the response body itself
  streamOverviewAgentChat: async (body, signal) => {
    const response = await authFetch(
      `${API_URL}/api/quantity-surveyor-overview-agent/chat`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
        signal,
      },
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message ||
          `Failed to send overview agent message: ${response.status}`,
      );
    }
    return response;
  },

  streamOverviewAgentConfirm: async (body, signal) => {
    const response = await authFetch(
      `${API_URL}/api/quantity-surveyor-overview-agent/confirm`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
        signal,
      },
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message ||
          `Failed to confirm overview agent tool call: ${response.status}`,
      );
    }
    return response;
  },

  // abortMultipartVideo: async (key, uploadId) => {
  //   const response = await authFetch(
  //     `${API_URL}/api/uploads/video/multipart/abort`,
  //     {
  //       method: "POST",
  //       headers: {
  //         "Content-Type": "application/json",
  //       },
  //       body: JSON.stringify({ key, uploadId }),
  //     }
  //   );
  //   if (!response.ok)
  //     throw new Error(`Abort multipart failed: ${response.status}`);
  //   return response.json();
  // },

  // finalizeVideoUpload: async (projectId, sectionId, documentationId, s3Key) => {
  //   const response = await authFetch(`${API_URL}/api/uploads/video/finalize`, {
  //     method: "POST",
  //     headers: {
  //       "Content-Type": "application/json",
  //     },
  //     body: JSON.stringify({ documentationId, s3Key, projectId, sectionId }),
  //   });
  //   if (!response.ok) {
  //     const errorData = await response.json().catch(() => ({}));
  //     throw new Error(
  //       errorData.message || `Finalize failed: ${response.status}`
  //     );
  //   }
  //   return response.json();
  // },

  mergeDetectedObjects: async (
    projectId,
    sectionId,
    primaryId,
    mergeIds,
    floorPlanId,
  ) => {
    try {
      const response = await authFetch(
        `${API_URL}/api/projects/${projectId}/sections/${sectionId}/detected-objects/merge`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ primaryId, mergeIds, floorPlanId }),
        },
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || `Failed to merge objects: ${response.status}`,
        );
      }

      return response.json();
    } catch (error) {
      console.error("Error merging detected objects:", error);
      bugsnagClient.notify(error);
      throw error;
    }
  },

  // Add other API calls here

  get: (endpoint) => axios.get(`${API_URL}${endpoint}`),
  post: (endpoint, data) => axios.post(`${API_URL}${endpoint}`, data),

  getSignedKeyframeUrl: async (jobId, keyframeFilename) => {
    try {
      const response = await authFetch(`${API_URL}/api/keyframes/signed-url`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          jobId,
          keyframeFilename,
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to get signed URL: ${response.status}`);
      }

      return response.json();
    } catch (error) {
      console.error("API error in getSignedKeyframeUrl:", error);
      bugsnagClient.notify(error);
      throw error;
    }
  },

  fetchProjectVideoJobs: async (projectId) => {
    const response = await authFetch(
      `${API_URL}/api/uploads/project-jobs/${projectId}`,
    );
    const data = await response.json();
    return {
      projectId: data.projectId,
      jobs: data.videoJobs || [], // Return the videoJobs array from the response
    };
  },

  uploadPlan: async (projectId, formData) => {
    const response = await authFetch(`${API_URL}/api/uploads/plan`, {
      method: "POST",
      body: formData,
    });
    return response.json();
  },

  fetchProjectPlans: async (projectId) => {
    const response = await authFetch(
      `${API_URL}/api/uploads/project-plans/${projectId}`,
    );
    return response.json();
  },

  // Detected Objects endpoints
  fetchDetectedObjects: async (projectId, params = {}) => {
    try {
      const queryParams = new URLSearchParams();

      // Add optional parameters
      if (params.status) queryParams.append("status", params.status);
      if (params.type) queryParams.append("type", params.type);
      if (params.frameReference)
        queryParams.append("frameReference", params.frameReference);
      if (params.page) queryParams.append("page", params.page);
      if (params.limit) queryParams.append("limit", params.limit);
      if (params.sortBy) queryParams.append("sortBy", params.sortBy);
      if (params.sortOrder) queryParams.append("sortOrder", params.sortOrder);

      const queryString = queryParams.toString();
      const url = `${API_URL}/api/projects/${projectId}/detected-objects${
        queryString ? `?${queryString}` : ""
      }`;

      const response = await authFetch(url);

      if (!response.ok) {
        throw new Error(`Failed to fetch detected objects: ${response.status}`);
      }

      return adapter.detectedObjects.list((await response.json()).data);
    } catch (error) {
      console.error("Error fetching detected objects:", error);
      bugsnagClient.notify(error);
      throw error;
    }
  },

  fetchDetectedObjectStats: async (projectId) => {
    try {
      const response = await authFetch(
        `${API_URL}/api/projects/${projectId}/detected-objects/stats`,
      );

      if (!response.ok) {
        throw new Error(
          `Failed to fetch detected object stats: ${response.status}`,
        );
      }

      return response.json();
    } catch (error) {
      console.error("Error fetching detected object stats:", error);
      bugsnagClient.notify(error);
      throw error;
    }
  },

  fetchDetectedObjectsBySection: async (projectId, sectionId, params = {}) => {
    try {
      const queryParams = new URLSearchParams();

      // Add optional parameters
      if (params.status) queryParams.append("status", params.status);
      if (params.type) queryParams.append("type", params.type);
      if (params.frameReference)
        queryParams.append("frameReference", params.frameReference);
      if (params.page) queryParams.append("page", params.page);
      if (params.limit) queryParams.append("limit", params.limit);
      if (params.sortBy) queryParams.append("sortBy", params.sortBy);
      if (params.sortOrder) queryParams.append("sortOrder", params.sortOrder);

      const queryString = queryParams.toString();
      const url = `${API_URL}/api/projects/${projectId}/sections/${sectionId}/detected-objects${
        queryString ? `?${queryString}` : ""
      }`;

      const response = await authFetch(url);

      if (!response.ok) {
        throw new Error(
          `Failed to fetch detected objects by section: ${response.status}`,
        );
      }

      return adapter.detectedObjects.list(await response.json());
    } catch (error) {
      console.error("Error fetching detected objects by section:", error);
      bugsnagClient.notify(error);
      throw error;
    }
  },

  fetchDetectedObjectsByDocumentation: async (documentationId) => {
    try {
      const url = `${API_URL}/api/projects/documentation/${documentationId}/detected-objects`;

      const response = await authFetch(url);

      if (!response.ok) {
        throw new Error(
          `Failed to fetch detected objects by documentation: ${response.status}`,
        );
      }

      return adapter.detectedObjects.list(await response.json());
    } catch (error) {
      console.error("Error fetching detected objects by documentation:", error);
      bugsnagClient.notify(error);
      throw error;
    }
  },

  addFrameObjectToDocumentation: async (documentationId, frameId, data) => {
    try {
      const response = await authFetch(
        `${API_URL}/api/documentation/${documentationId}/${frameId}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(adapter.documentation.addFrameObject(data)),
        },
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message || `Failed to add frame object: ${response.status}`,
        );
      }

      return response.json();
    } catch (error) {
      console.error("Error adding frame object to documentation:", error);
      bugsnagClient.notify(error);
      throw error;
    }
  },

  addCableOnlyToDocumentation: async (documentationId, frameId, data) => {
    try {
      const response = await authFetch(
        `${API_URL}/api/documentation/${documentationId}/${frameId}/cable-only`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(data),
        },
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message || `Failed to mark cable: ${response.status}`,
        );
      }

      return response.json();
    } catch (error) {
      console.error("Error marking cable only:", error);
      bugsnagClient.notify(error);
      throw error;
    }
  },

  fetchDetectedObject: async (projectId, objectId) => {
    try {
      const response = await authFetch(
        `${API_URL}/api/projects/${projectId}/detected-objects/${objectId}`,
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch detected object: ${response.status}`);
      }

      return response.json();
    } catch (error) {
      console.error("Error fetching detected object:", error);
      bugsnagClient.notify(error);
      throw error;
    }
  },

  updateDetectedObjectStatus: async (
    projectId,
    objectId,
    status,
    notes = "",
  ) => {
    try {
      const response = await authFetch(
        `${API_URL}/api/projects/${projectId}/detected-objects/${objectId}/status`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ status, notes }),
        },
      );

      if (!response.ok) {
        throw new Error(
          `Failed to update detected object status: ${response.status}`,
        );
      }

      return response.json();
    } catch (error) {
      console.error("Error updating detected object status:", error);
      bugsnagClient.notify(error);
      throw error;
    }
  },

  fetchDetectedObjectsByFrame: async (projectId, frameReference) => {
    try {
      const response = await authFetch(
        `${API_URL}/api/projects/${projectId}/detected-objects/by-frame/${frameReference}`,
      );

      if (!response.ok) {
        throw new Error(
          `Failed to fetch detected objects by frame: ${response.status}`,
        );
      }

      return response.json();
    } catch (error) {
      console.error("Error fetching detected objects by frame:", error);
      bugsnagClient.notify(error);
      throw error;
    }
  },

  fetchDetectedObjectsByLvItem: async (projectId, lvItemId) => {
    try {
      // Filter detected objects that match the LV item ID
      const response = await api.fetchDetectedObjects(projectId, {
        status: "installed",
      });

      if (response.success && response.data) {
        // Filter objects that match the LV item ID
        const filteredObjects = response.data.filter(
          (obj) => obj.lvItemId === lvItemId,
        );
        return {
          ...response,
          data: filteredObjects,
          count: filteredObjects.length,
        };
      }

      return response;
    } catch (error) {
      console.error("Error fetching detected objects by LV item:", error);
      bugsnagClient.notify(error);
      throw error;
    }
  },

  // Connect detected objects to an LV item by group_id within a section
  connectDetectedObjectsToLvByLayer: async (
    projectId,
    lvItemId,
    groupId,
    floorPlanId,
  ) => {
    try {
      const response = await authFetch(
        `${API_URL}/api/projects/${projectId}/detected-objects/connect-lv`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(
            adapter.detectedObjects.connectDetectedObjectsToLvByLayer({
              lvItemId,
              groupId,
              floorPlanId,
            }),
          ),
        },
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || `Failed to connect objects: ${response.status}`,
        );
      }
      return response.json();
    } catch (error) {
      console.error("Error connecting detected objects to LV:", error);
      throw error;
    }
  },

  createDetectedObject: async (projectId, sectionId, objectData) => {
    try {
      const response = await authFetch(
        `${API_URL}/api/projects/${projectId}/sections/${sectionId}/detected-objects`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(adapter.detectedObjects.create(objectData)),
        },
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message ||
            `Failed to create detected object: ${response.status}`,
        );
      }

      return adapter.detectedObjects.createResponse(await response.json());
    } catch (error) {
      console.error("Error creating detected object:", error);
      bugsnagClient.notify(error);
      throw error;
    }
  },

  // Delete a detected object and cascade bbox cleanup
  deleteDetectedObject: async (
    projectId,
    sectionId,
    detectedObjectId,
    isDeviation = true,
  ) => {
    try {
      const response = await authFetch(
        `${API_URL}/api/projects/${projectId}/sections/${sectionId}/detected-objects/${detectedObjectId}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ isDeviation }),
        },
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message ||
            `Failed to delete detected object: ${response.status}`,
        );
      }

      return await response.json();
    } catch (error) {
      console.error("Error deleting detected object:", error);
      bugsnagClient.notify(error);
      throw error;
    }
  },

  // Delete a floor plan element
  deleteFloorPlanElement: async (floorPlanId, elementId) => {
    try {
      const response = await authFetch(
        `${API_URL}/api/projects/sections/plans/${floorPlanId}/elements/${elementId}`,
        {
          method: "DELETE",
        },
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message ||
            `Failed to delete floor plan element: ${response.status}`,
        );
      }

      return await response.json();
    } catch (error) {
      console.error("Error deleting floor plan element:", error);
      bugsnagClient.notify(error);
      throw error;
    }
  },

  // Fetch Leistungsverzeichnis data for a project
  fetchLV: async (projectId) => {
    try {
      console.log("Fetch LV for project ", projectId);

      const response = await authFetch(
        `${API_URL}/api/projects/${projectId}/lv`,
      );
      if (response.status === 404) {
        console.log(`No LV found for project ${projectId}, returning empty.`);
        return { items: [], _id: null };
      }
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || `Failed to fetch LV: ${response.status}`,
        );
      }
      return adapter.lv.getLV(await response.json());
    } catch (error) {
      console.error("API error fetching LV:", error);
      bugsnagClient.notify(error);
      throw error;
    }
  },

  // Upload Leistungsverzeichnis file
  uploadLV: async (projectId, formData) => {
    try {
      const response = await authFetch(
        `${API_URL}/api/projects/${projectId}/lv/upload`,
        {
          method: "POST",
          body: formData,
        },
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || `Failed to upload LV: ${response.status}`,
        );
      }

      return await response.json();
    } catch (error) {
      console.error("API error uploading LV:", error);
      bugsnagClient.notify(error);
      throw error;
    }
  },

  // Save processed LV data
  saveLVData: async (projectId, lvDataItems) => {
    try {
      const response = await authFetch(
        `${API_URL}/api/projects/${projectId}/lv`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ items: lvDataItems }),
        },
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || `Failed to save LV data: ${response.status}`,
        );
      }

      return await response.json();
    } catch (error) {
      console.error("API error saving LV data:", error);
      bugsnagClient.notify(error);
      throw error;
    }
  },

  // Add a function for updating an item if it doesn't exist
  updateLVItem: async (projectId, itemId, updateData) => {
    try {
      const response = await authFetch(
        `${API_URL}/api/projects/${projectId}/lv/items/${itemId}`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(updateData),
        },
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || `Failed to update LV item: ${response.status}`,
        );
      }

      return await response.json();
    } catch (error) {
      console.error("API error updating LV item:", error);
      bugsnagClient.notify(error);
      throw error;
    }
  },

  // Add new LV item
  addLVItem: async (projectId, itemData) => {
    try {
      const response = await authFetch(
        `${API_URL}/api/projects/${projectId}/lv/items`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(adapter.lv.addItem(itemData)),
        },
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || `Failed to add LV item: ${response.status}`,
        );
      }

      return await response.json();
    } catch (error) {
      console.error("API error adding LV item:", error);
      bugsnagClient.notify(error);
      throw error;
    }
  },

  // Delete LV item
  deleteLVItem: async (projectId, itemId) => {
    try {
      const response = await authFetch(
        `${API_URL}/api/projects/${projectId}/lv/items/${itemId}`,
        {
          method: "DELETE",
        },
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || `Failed to delete LV item: ${response.status}`,
        );
      }

      return await response.json();
    } catch (error) {
      console.error("API error deleting LV item:", error);
      bugsnagClient.notify(error);
      throw error;
    }
  },

  // Export LV as DA11 (GAEB Aufmaß) format
  exportLVAsDA11: async (projectId) => {
    try {
      const response = await authFetch(
        `${API_URL}/api/projects/${projectId}/lv/export/da11`,
      );

      if (!response.ok) {
        throw new Error(`Failed to export DA11: ${response.status}`);
      }

      // Extract filename from Content-Disposition header
      const disposition = response.headers.get("Content-Disposition");
      const match = disposition && disposition.match(/filename="(.+)"/);
      const fileName = match ? match[1] : `Aufmass_${projectId}.d11`;

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("API error exporting DA11:", error);
      bugsnagClient.notify(error);
      throw error;
    }
  },

  // Project Team Management
  fetchProjectTeam: async (projectId) => {
    try {
      const response = await authFetch(
        `${API_URL}/api/projects/${projectId}/team`,
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message ||
            `Failed to fetch project team: ${response.status}`,
        );
      }

      return adapter.project.getTeam(await response.json());
    } catch (error) {
      console.error("API error fetching project team:", error);
      bugsnagClient.notify(error);
      throw error;
    }
  },

  fetchAvailableMembers: async (projectId) => {
    try {
      const response = await authFetch(
        `${API_URL}/api/projects/${projectId}/available-members`,
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message ||
            `Failed to fetch available members: ${response.status}`,
        );
      }

      return adapter.project.getAvailableMembers(await response.json());
    } catch (error) {
      console.error("API error fetching available members:", error);
      bugsnagClient.notify(error);
      throw error;
    }
  },

  addProjectTeamMember: async (projectId, memberData) => {
    try {
      const response = await authFetch(
        `${API_URL}/api/projects/${projectId}/team`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(adapter.project.addTeamMember(memberData)),
        },
      );

      if (!response.ok) {
        let errorData;
        const contentType = response.headers.get("content-type");

        if (contentType && contentType.includes("application/json")) {
          try {
            errorData = await response.json();
            console.log("API: Error response data:", errorData);
          } catch (parseError) {
            console.error(
              "API: Failed to parse error response as JSON:",
              parseError,
            );
            errorData = {};
          }
        } else {
          // Handle non-JSON error responses
          try {
            const textResponse = await response.text();
            console.log("API: Non-JSON error response:", textResponse);
            errorData = { message: textResponse };
          } catch (textError) {
            console.error(
              "API: Failed to read error response as text:",
              textError,
            );
            errorData = {};
          }
        }

        const errorMessage =
          errorData.message ||
          errorData.error ||
          `Server returned ${response.status}: ${response.statusText}`;

        console.log("API: Final error message:", errorMessage);
        throw new Error(errorMessage);
      }

      const result = await response.json();
      console.log("API: Success response:", result);
      return result;
    } catch (error) {
      console.error("API: Error adding team member:", error);
      // Re-throw the error with preserved message
      bugsnagClient.notify(error);
      throw error;
    }
  },

  updateProjectTeamMemberRole: async (projectId, memberId, role) => {
    try {
      const response = await authFetch(
        `${API_URL}/api/projects/${projectId}/team/${memberId}`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ role }),
        },
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message ||
            `Failed to update member role: ${response.status}`,
        );
      }

      return await response.json();
    } catch (error) {
      console.error("API error updating member role:", error);
      bugsnagClient.notify(error);
      throw error;
    }
  },

  removeProjectTeamMember: async (projectId, memberId) => {
    try {
      const response = await authFetch(
        `${API_URL}/api/projects/${projectId}/team/${memberId}`,
        {
          method: "DELETE",
        },
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message ||
            `Failed to remove team member: ${response.status}`,
        );
      }

      return await response.json();
    } catch (error) {
      console.error("API error removing team member:", error);
      bugsnagClient.notify(error);
      throw error;
    }
  },

  addDetectedItemsToLV: async (projectId, jobId) => {
    try {
      console.log("Add detected items to LV for job ", jobId);

      const response = await authFetch(
        `${API_URL}/api/projects/${projectId}/jobs/${jobId}/detect-items`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({}),
        },
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message ||
            `Failed to add detected items: ${response.status}`,
        );
      }

      return await response.json();
    } catch (error) {
      console.error("API error in addDetectedItemsToLV:", error);
      bugsnagClient.notify(error);
      throw error;
    }
  },

  fetchObjectData: async (projectId, jobId, objectKey) => {
    try {
      const response = await authFetch(
        `${API_URL}/api/projects/${projectId}/jobs/${jobId}/objects/${objectKey}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to fetch object data");
      }

      return await response.json();
    } catch (error) {
      console.error("API error fetching object data:", error);
      bugsnagClient.notify(error);
      throw error;
    }
  },

  handleAuthError: (error) => {
    if (
      error.response &&
      (error.response.status === 401 || error.response.status === 403)
    ) {
      // Token is invalid or expired
      localStorage.removeItem("token");
      window.location.href = "/login";
    }
    throw error;
  },

  askLVChatbot: async (projectId, message) => {
    try {
      const response = await authFetch(
        `${API_URL}/api/projects/${projectId}/lv/chat`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ message }),
        },
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || `Chatbot request failed: ${response.status}`,
        );
      }

      return await response.json(); // Should return { answer: "..." }
    } catch (error) {
      console.error("API error asking LV chatbot:", error);
      bugsnagClient.notify(error);
      throw error; // Re-throw to be caught by the component
    }
  },

  transcribeAudio: async (audioBlob, language = "de") => {
    try {
      const formData = new FormData();
      formData.append("audio", audioBlob, "recording.webm");
      formData.append("language", language);

      const response = await authFetch(`${API_URL}/api/ai/speech-to-text`, {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || `Transcription failed: ${response.status}`,
        );
      }

      return await response.json(); // Should return { transcript: "...", success: true }
    } catch (error) {
      console.error("API error transcribing audio:", error);
      bugsnagClient.notify(error);
      throw error; // Re-throw to be caught by the component
    }
  },

  getSphericalVisualizationUrl: async (jobId, frameName, options = {}) => {
    try {
      const { quality, width } = options;

      // Build query parameters for optimization
      const queryParams = new URLSearchParams();
      if (quality) queryParams.set("quality", quality.toString());
      if (width) queryParams.set("width", width.toString());

      // Use the direct image serving endpoint instead of signed URLs
      const directImageUrl = `${API_URL}/api/keyframes/spherical-visualization/${jobId}/${frameName}${
        queryParams.toString() ? "?" + queryParams.toString() : ""
      }`;

      // Test if the image exists by making a HEAD request
      const testResponse = await authFetch(directImageUrl, {
        method: "HEAD",
      });

      if (!testResponse.ok) {
        throw new Error(
          `Failed to get spherical visualization: ${testResponse.status}`,
        );
      }

      // Return the direct URL
      return { url: directImageUrl };
    } catch (error) {
      console.error("API error in getSphericalVisualizationUrl:", error);
      bugsnagClient.notify(error);
      throw error;
    }
  },

  // Helper function for fast loading (lower quality)
  getSphericalVisualizationUrlFast: async (jobId, frameName) => {
    return api.getSphericalVisualizationUrl(jobId, frameName, {
      quality: 70,
      width: 2048,
    });
  },

  // Helper function for high quality loading
  getSphericalVisualizationUrlHQ: async (jobId, frameName) => {
    return api.getSphericalVisualizationUrl(jobId, frameName);
  },

  getSphericalVisualizationImageUrl: (jobId, frameName) => {
    return `${API_URL}/api/keyframes/spherical-visualization/${jobId}/${frameName}`;
  },

  getFrameImageUrl: (projectId, documentation, frameName) => {
    if (documentation.framesPath) {
      return `${API_URL}/api/documentation/${documentation.framesPath}/${frameName}`;
    }
    return `${API_URL}/api/documentation/project/${projectId}/${
      documentation._id || documentation.documentationId
    }/image/${frameName}`;
  },

  // TODO: This file has grown too large (~70kb) and should be split.
  // Importing a single utility currently pulls in thousands of lines.
  // For now, this helper lives with documentation-related logic to
  // preserve existing behavior until the refactor is complete.
  getDocumentationPreviewImageUrl: ({
    documentationId,
    documentationPreviewPath,
  }) => {
    if (!documentationId || !documentationPreviewPath) return;
    return `${API_URL}/api/documentation/${documentationId}/preview`;
  },

  getReconstructionJsonUrl: async (jobId) => {
    try {
      console.log(`Calling reconstruction API for jobId: ${jobId}`);
      const response = await authFetch(
        `${API_URL}/api/opensfm/reconstruction/${jobId}`,
        {
          method: "GET",
        },
      );

      console.log(`API response status: ${response.status}`);

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`API error response:`, errorText);
        throw new Error(
          `Failed to get reconstruction data: ${response.status} - ${errorText}`,
        );
      }

      const result = await response.json();
      console.log(`API returned reconstruction data:`, result);
      return result;
    } catch (error) {
      console.error("API error in getReconstructionJsonUrl:", error);
      bugsnagClient.notify(error);
      throw error;
    }
  },

  // Documentation endpoints
  getDocumentationByProject: async (projectId, params = {}) => {
    const queryParams = new URLSearchParams(params).toString();
    const url = `${API_URL}/api/documentation/project/${projectId}${
      queryParams ? `?${queryParams}` : ""
    }`;

    const response = await authFetch(url);

    if (!response.ok) {
      throw new Error(`Failed to fetch documentation: ${response.statusText}`);
    }

    return response.json();
  },

  getDocumentationBySection: async (sectionId, params = {}) => {
    const queryParams = new URLSearchParams(params).toString();
    const url = `${API_URL}/api/documentation/section/${sectionId}${
      queryParams ? `?${queryParams}` : ""
    }`;

    const response = await authFetch(url);

    if (!response.ok) {
      throw new Error(
        `Failed to fetch section documentation: ${response.statusText}`,
      );
    }

    return adapter.documentation.list(await response.json());
  },

  getDocumentation: async (id) => {
    const response = await authFetch(`${API_URL}/api/documentation/${id}`);

    if (!response.ok) {
      throw new Error(`Failed to fetch documentation: ${response.statusText}`);
    }

    return adapter.documentation.get(await response.json());
  },

  createDocumentation: async (data) => {
    const response = await authFetch(`${API_URL}/api/documentation`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw new Error(`Failed to create documentation: ${response.statusText}`);
    }

    return response.json();
  },

  updateDocumentation: async (id, data) => {
    const response = await authFetch(`${API_URL}/api/documentation/${id}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw new Error(`Failed to update documentation: ${response.statusText}`);
    }

    return response.json();
  },

  updateDocumentationStatus: async (id, status) => {
    const response = await authFetch(
      `${API_URL}/api/documentation/${id}/status`,
      {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ status }),
      },
    );

    if (!response.ok) {
      throw new Error(
        `Failed to update documentation status: ${response.statusText}`,
      );
    }

    return response.json();
  },

  deleteDocumentation: async (id) => {
    const response = await authFetch(`${API_URL}/api/documentation/${id}`, {
      method: "DELETE",
    });

    if (!response.ok) {
      throw new Error(`Failed to delete documentation: ${response.statusText}`);
    }

    return response.json();
  },

  addDocumentationFrame: async (id, frameData) => {
    const response = await authFetch(
      `${API_URL}/api/documentation/${id}/frames`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(frameData),
      },
    );

    if (!response.ok) {
      throw new Error(`Failed to add frame: ${response.statusText}`);
    }

    return response.json();
  },

  updateDocumentationFrame: async (id, frameIndex, frameData) => {
    const response = await authFetch(
      `${API_URL}/api/documentation/${id}/frames/${frameIndex}`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(frameData),
      },
    );

    if (!response.ok) {
      throw new Error(`Failed to update frame: ${response.statusText}`);
    }

    return response.json();
  },

  patchDocumentationFrame: async (frameId, frameData) => {
    const response = await authFetch(
      `${API_URL}/api/documentation/frames/${frameId}`,
      {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(frameData),
      },
    );

    if (!response.ok) {
      throw new Error(`Failed to patch frame: ${response.statusText}`);
    }

    return response.json();
  },

  deleteDocumentationFrame: async (id, frameIndex) => {
    const response = await authFetch(
      `${API_URL}/api/documentation/${id}/frames/${frameIndex}`,
      {
        method: "DELETE",
      },
    );

    if (!response.ok) {
      throw new Error(`Failed to delete frame: ${response.statusText}`);
    }

    return response.json();
  },

  getDocumentationStats: async (projectId) => {
    const response = await authFetch(
      `${API_URL}/api/documentation/project/${projectId}/stats`,
    );

    if (!response.ok) {
      throw new Error(
        `Failed to fetch documentation stats: ${response.statusText}`,
      );
    }

    return response.json();
  },

  getDocumentationWithCounts: async (projectId) => {
    const response = await authFetch(
      `${API_URL}/api/documentation/project/${projectId}/with-counts`,
    );

    if (!response.ok) {
      throw new Error(
        `Failed to fetch documentation with counts: ${response.statusText}`,
      );
    }

    return response.json();
  },

  uploadDocumentationPreview: async (documentationId, preview) => {
    try {
      const formData = new FormData();
      formData.append("preview", preview);

      const response = await authFetch(
        `${API_URL}/api/uploads/documentation/${documentationId}/preview`,
        {
          method: "POST",
          body: formData,
        },
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message ||
            `Failed to upload documentation preview: ${response.status}`,
        );
      }

      return await response.json();
    } catch (error) {
      console.error("API error uploading documentation preview:", error);
      bugsnagClient.notify(error);
      throw error;
    }
  },

  aiChat: async (
    message,
    projectId = null,
    sectionId = null,
    conversationHistory = null,
  ) => {
    try {
      const requestBody = {
        message,
      };

      // Add projectId and sectionId if provided
      if (projectId) {
        requestBody.projectId = projectId;
      }
      if (sectionId) {
        requestBody.sectionId = sectionId;
      }

      // Add conversation history if provided
      if (conversationHistory) {
        requestBody.conversationHistory = conversationHistory;
      }

      const response = await authFetch(`${API_URL}/api/ai/rag-chat`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(adapter.detectedObjects.chat(requestBody)),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || `AI chat request failed: ${response.status}`,
        );
      }

      return await response.json(); // Should return { answer: "...", success: true }
    } catch (error) {
      console.error("API error in AI chat:", error);
      bugsnagClient.notify(error);
      throw error;
    }
  },

  researchChat: async (
    message,
    projectId,
    conversationHistory = null,
    attachedPages = null,
    images = null,
  ) => {
    try {
      const requestBody = { message, projectId };

      if (conversationHistory) {
        requestBody.conversationHistory = conversationHistory;
      }

      if (attachedPages) {
        requestBody.attachedPages = attachedPages;
      }

      if (images) {
        requestBody.images = images;
      }

      const response = await authFetch(`${API_URL}/api/ai/research-chat`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message ||
            `Research chat request failed: ${response.status}`,
        );
      }

      return await response.json();
    } catch (error) {
      console.error("API error in research chat:", error);
      bugsnagClient.notify(error);
      throw error;
    }
  },

  fetchSectionPlan: async (projectId, sectionId) => {
    try {
      const response = await authFetch(
        `${API_URL}/api/projects/${projectId}/sections/${sectionId}/plan`,
      );
      if (!response.ok) {
        throw new Error(`Failed to fetch section plan: ${response.status}`);
      }

      return adapter.section.getPlan(await response.json());
    } catch (error) {
      console.error("Error fetching section plan:", error);
      bugsnagClient.notify(error);
      throw error;
    }
  },

  createSection: async (projectId, data) => {
    try {
      const response = await authFetch(
        `${API_URL}/api/projects/${projectId}/sections`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(data),
        },
      );
      if (!response.ok) {
        throw new Error(`Failed to create section: ${response.statusText}`);
      }
      return adapter.section.create(await response.json());
    } catch (error) {
      console.error("Error creating section:", error);
      bugsnagClient.notify(error);
      throw error;
    }
  },

  fetchSections: async (projectId) => {
    try {
      const response = await authFetch(
        `${API_URL}/api/projects/${projectId}/sections`,
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch sections: ${response.statusText}`);
      }

      return adapter.section.list(await response.json());
    } catch (error) {
      console.error("Error fetching sections:", error);
      bugsnagClient.notify(error);
      throw error;
    }
  },

  getPlan: async (projectId, sectionId) => {
    try {
      const response = await authFetch(
        `${API_URL}/api/projects/${projectId}/sections/${sectionId}/plan`,
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch plan: ${response.statusText}`);
      }

      const planData = await response.json();
      return adapter.section.getPlan(planData);
    } catch (error) {
      console.warn(`Failed to fetch plan for section ${sectionId}:`, error);
      bugsnagClient.notify(error);
      throw error;
    }
  },

  uploadSectionPlan: async (projectId, sectionId, formData) => {
    try {
      const response = await authFetch(
        `${API_URL}/api/projects/${projectId}/sections/${sectionId}/plans`,
        {
          method: "POST",
          body: formData,
        },
      );
      if (!response.ok) {
        throw new Error(
          `Failed to upload section plan: ${response.statusText}`,
        );
      }
      return adapter.section.uploadPlanResponse(await response.json());
    } catch (error) {
      console.error("Error uploading section plan:", error);
      bugsnagClient.notify(error);
      throw error;
    }
  },

  updateFloorPlanStatus: async (floorPlanId, status) => {
    try {
      const response = await authFetch(
        `${API_URL}/api/projects/sections/plans/${floorPlanId}/status`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ status }),
        },
      );
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message ||
            `Failed to update floor plan status: ${response.statusText}`,
        );
      }
      return await response.json();
    } catch (error) {
      console.error("Error updating floor plan status:", error);
      bugsnagClient.notify(error);
      throw error;
    }
  },

  updateFloorPlanPreview: async (floorPlanId, file) => {
    try {
      const formData = new FormData();
      formData.append("preview", file);

      const response = await authFetch(
        `${API_URL}/api/projects/sections/plans/${floorPlanId}/preview`,
        {
          method: "POST",
          body: formData,
        },
      );
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message ||
            `Failed to update floor plan preview: ${response.statusText}`,
        );
      }
      return await response.json();
    } catch (error) {
      console.error("Error updating floor plan preview:", error);
      bugsnagClient.notify(error);
      throw error;
    }
  },

  updateFloorPlanChessboard: async (floorPlanId, chessboard) => {
    try {
      const response = await authFetch(
        `${API_URL}/api/projects/sections/plans/${floorPlanId}/chessboard`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ chessboard }),
        },
      );
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message ||
            `Failed to update floor plan chessboard: ${response.statusText}`,
        );
      }
      return await response.json();
    } catch (error) {
      console.error("Error updating floor plan chessboard:", error);
      bugsnagClient.notify(error);
      throw error;
    }
  },

  // Multi-line cables endpoints
  getMultiLineCables: async (planId) => {
    try {
      const response = await authFetch(
        `${API_URL}/api/multi-line-cables?planId=${planId}`,
      );
      if (!response.ok) {
        throw new Error(
          `Failed to fetch multi-line cables: ${response.statusText}`,
        );
      }
      return await response.json();
    } catch (error) {
      console.error("Error fetching multi-line cables:", error);
      bugsnagClient.notify(error);
      throw error;
    }
  },

  getMultiLineCablesByElementIds: async (elementIds) => {
    try {
      const ids = Array.isArray(elementIds) ? elementIds : [];
      const query = ids.length ? `?elementIds=${ids.join(",")}` : "";
      const response = await authFetch(
        `${API_URL}/api/multi-line-cables/by-elements${query}`,
      );
      if (!response.ok) {
        throw new Error(
          `Failed to fetch multi-line cables by elements: ${response.statusText}`,
        );
      }
      return await response.json();
    } catch (error) {
      console.error("Error fetching multi-line cables by elements:", error);
      bugsnagClient.notify(error);
      throw error;
    }
  },

  getOrphanedCables: async (documentationId) => {
    try {
      const response = await authFetch(
        `${API_URL}/api/detected-objects/documentation/${documentationId}/orphaned-cables`,
      );
      if (!response.ok) {
        throw new Error(
          `Failed to fetch orphaned cables: ${response.statusText}`,
        );
      }
      return await response.json();
    } catch (error) {
      console.error("Error fetching orphaned cables:", error);
      bugsnagClient.notify(error);
      throw error;
    }
  },

  deleteCable: async (documentationId, frameId, floorPlanElementId) => {
    try {
      const query = new URLSearchParams({
        frameId: String(frameId),
        floorPlanElementId: String(floorPlanElementId),
      });
      const response = await authFetch(
        `${API_URL}/api/projects/documentation/${documentationId}/cables?${query.toString()}`,
        {
          method: "DELETE",
        },
      );
      if (!response.ok) {
        throw new Error(`Failed to delete cable: ${response.statusText}`);
      }
      return await response.json();
    } catch (error) {
      console.error("Error deleting cable:", error);
      bugsnagClient.notify(error);
      throw error;
    }
  },

  getMultiLineCable: async (cableId) => {
    try {
      const response = await authFetch(
        `${API_URL}/api/multi-line-cables/${cableId}`,
      );
      if (!response.ok) {
        throw new Error(
          `Failed to fetch multi-line cable: ${response.statusText}`,
        );
      }
      return await response.json();
    } catch (error) {
      console.error("Error fetching multi-line cable:", error);
      bugsnagClient.notify(error);
      throw error;
    }
  },

  getMultiLineCableByDwgId: async (dwgId) => {
    try {
      const response = await authFetch(
        `${API_URL}/api/multi-line-cables/dwg/${dwgId}`,
      );
      if (!response.ok) {
        if (response.status === 404) {
          return null; // DWG element has no associated cable
        }
        throw new Error(
          `Failed to fetch multi-line cable for DWG: ${response.statusText}`,
        );
      }
      const result = await response.json();
      return result?.cable;
    } catch (error) {
      console.error("Error fetching multi-line cable for DWG:", error);
      bugsnagClient.notify(error);
      throw error;
    }
  },

  // Floor plan paths endpoints
  getFloorPlanGraph: async (planId) => {
    try {
      const response = await authFetch(
        `${API_URL}/api/floor-plan-paths/${planId}/graph`,
      );
      if (!response.ok) {
        throw new Error(
          `Failed to fetch floor plan graph: ${response.statusText}`,
        );
      }
      return await response.json();
    } catch (error) {
      console.error("Error fetching floor plan graph:", error);
      bugsnagClient.notify(error);
      throw error;
    }
  },

  // Quantity Surveyor Approvals
  getQuantitySurveyorApprovals: async (projectId) => {
    const response = await authFetch(
      `${API_URL}/api/quantity-surveyor-approvals?projectId=${projectId}`,
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || `Failed to fetch approvals: ${response.status}`,
      );
    }
    return response.json();
  },

  bulkSetQuantitySurveyorLvMatch: async ({
    projectId,
    elementTypeIds,
    routeTypeIds,
    lvMatchId,
    lvMatchName,
    lvMatchReasoning,
  }) => {
    const response = await authFetch(
      `${API_URL}/api/quantity-surveyor-lv-matches/bulk-set`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          projectId,
          elementTypeIds,
          routeTypeIds,
          lvMatchId,
          lvMatchName,
          lvMatchReasoning,
        }),
      },
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || `Failed to set LV position: ${response.status}`,
      );
    }
    return response.json();
  },

  bulkApproveElementTypes: async ({ projectId, elementTypeIds, approved }) => {
    const response = await authFetch(
      `${API_URL}/api/quantity-surveyor-approvals/bulk-approve-element-types`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ projectId, elementTypeIds, approved }),
      },
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message ||
          `Failed to bulk approve element types: ${response.status}`,
      );
    }
  },

  bulkApproveRouteTypes: async ({ projectId, routeTypeIds, approved }) => {
    const response = await authFetch(
      `${API_URL}/api/quantity-surveyor-approvals/bulk-approve-route-types`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ projectId, routeTypeIds, approved }),
      },
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message ||
          `Failed to bulk approve route types: ${response.status}`,
      );
    }
  },

  bulkApproveQuantityTypes: async ({
    projectId,
    diagramItemTypeIds,
    approved,
  }) => {
    const response = await authFetch(
      `${API_URL}/api/quantity-surveyor-approvals/bulk-approve-diagram-item-types`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ projectId, diagramItemTypeIds, approved }),
      },
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message ||
          `Failed to bulk approve quantity types: ${response.status}`,
      );
    }
  },

  toggleQuantitySurveyorApproval: async (data) => {
    const response = await authFetch(
      `${API_URL}/api/quantity-surveyor-approvals/toggle`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      },
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || `Failed to toggle approval: ${response.status}`,
      );
    }
    return response.json();
  },

  /**
   * @param {{ projectId: number; floorPlanId?: number | null; diagramId?: number | null; limit?: number; page?: number; snapshotMaxId?: number | null }} args
   */
  getQuantitySurveyorHistory: async ({
    projectId,
    floorPlanId,
    diagramId,
    limit,
    page,
    snapshotMaxId,
  }) => {
    const params = new URLSearchParams({ projectId: String(projectId) });
    if (typeof floorPlanId === "number")
      params.set("floorPlanId", String(floorPlanId));
    if (typeof diagramId === "number")
      params.set("diagramId", String(diagramId));
    if (typeof limit === "number") params.set("limit", String(limit));
    if (typeof page === "number") params.set("page", String(page));
    if (typeof snapshotMaxId === "number")
      params.set("snapshotMaxId", String(snapshotMaxId));
    const response = await authFetch(
      `${API_URL}/api/quantity-surveyor-history?${params.toString()}`,
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || `Failed to fetch history: ${response.status}`,
      );
    }
    return response.json();
  },

  /**
   * @param {{ projectId: number; floorPlanId: number | null; diagramId: number | null; sinceEntryId: number | null }} args
   */
  getOwnUndoState: async ({
    projectId,
    floorPlanId,
    diagramId,
    sinceEntryId,
  }) => {
    const params = new URLSearchParams({ projectId: String(projectId) });
    if (typeof floorPlanId === "number")
      params.set("floorPlanId", String(floorPlanId));
    if (typeof diagramId === "number")
      params.set("diagramId", String(diagramId));
    if (typeof sinceEntryId === "number")
      params.set("sinceEntryId", String(sinceEntryId));
    const response = await authFetch(
      `${API_URL}/api/quantity-surveyor-history/own-undo-state?${params.toString()}`,
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || `Failed to fetch undo state: ${response.status}`,
      );
    }
    return response.json();
  },

  // A refused revert is a 409 with `{ ok: false, revertibility }`; any other 409 (busy plan) is an error.
  revertQuantitySurveyorHistoryEntry: async (entryId) => {
    const response = await authFetch(
      `${API_URL}/api/quantity-surveyor-history/${entryId}/revert`,
      { method: "POST" },
    );
    if (response.status === 409) {
      const body = await response.json().catch(() => ({}));
      if (body.ok === false) return body;
      throw new Error(body.message || `Failed to revert entry: 409`);
    }
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || `Failed to revert entry: ${response.status}`,
      );
    }
    return response.json();
  },

  getSeenMarkerIds: async (elementTypeId) => {
    const response = await authFetch(
      `${API_URL}/api/quantity-surveyor-user-marker-states?elementTypeId=${elementTypeId}`,
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || `Failed to fetch seen markers: ${response.status}`,
      );
    }
    return response.json();
  },

  markMarkerSeen: async (markerId) => {
    const response = await authFetch(
      `${API_URL}/api/quantity-surveyor-user-marker-states/mark-seen`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ markerId }),
      },
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || `Failed to mark marker seen: ${response.status}`,
      );
    }
  },

  getSeenRouteIds: async (routeTypeId) => {
    const response = await authFetch(
      `${API_URL}/api/quantity-surveyor-user-route-states?routeTypeId=${routeTypeId}`,
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || `Failed to fetch seen routes: ${response.status}`,
      );
    }
    return response.json();
  },

  markRouteSeen: async (routeId) => {
    const response = await authFetch(
      `${API_URL}/api/quantity-surveyor-user-route-states/mark-seen`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ routeId }),
      },
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || `Failed to mark route seen: ${response.status}`,
      );
    }
  },

  getSeenDiagramOccurrenceIds: async (itemTypeId) => {
    const response = await authFetch(
      `${API_URL}/api/quantity-surveyor-user-diagram-occurrence-states?itemTypeId=${itemTypeId}`,
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message ||
          `Failed to fetch seen diagram occurrences: ${response.status}`,
      );
    }
    return response.json();
  },

  markDiagramOccurrenceSeen: async (occurrenceId) => {
    const response = await authFetch(
      `${API_URL}/api/quantity-surveyor-user-diagram-occurrence-states/mark-seen`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ occurrenceId }),
      },
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message ||
          `Failed to mark diagram occurrence seen: ${response.status}`,
      );
    }
  },

  renameRouteType: async (routeTypeId, cableType, applyToProject = false) => {
    const response = await authFetch(
      `${API_URL}/api/quantity-surveyor-route-types/${routeTypeId}/rename`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ cableType, applyToProject }),
      },
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || `Failed to rename route type: ${response.status}`,
      );
    }
    return response.json();
  },

  getQuantitySurveyorRouteTypeRenamePreview: async (routeTypeId, newName) => {
    const response = await authFetch(
      `${API_URL}/api/quantity-surveyor-route-types/${routeTypeId}/rename-preview?newName=${encodeURIComponent(newName)}`,
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message ||
          `Failed to load rename preview: ${response.status}`,
      );
    }
    return response.json();
  },

  updateRoute: async (routeId, data) => {
    const response = await authFetch(
      `${API_URL}/api/quantity-surveyor-routes/${routeId}`,
      {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      },
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || `Failed to update route: ${response.status}`,
      );
    }
    return response.json();
  },

  deleteRoute: async (routeId) => {
    const response = await authFetch(
      `${API_URL}/api/quantity-surveyor-routes/${routeId}`,
      {
        method: "DELETE",
      },
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || `Failed to delete route: ${response.status}`,
      );
    }
  },

  getCreditBalance: async () => {
    const response = await authFetch(`${API_URL}/api/credits/balance`, {
      headers: {
        "content-type": "application/json",
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message ||
          `Failed to fetch credit balance: ${response.status}`,
      );
    }

    return response.json();
  },
};

export default api;
